import './style.css';
import { audio } from './audio/audio';
import { randomSeed, seedFromString } from './core/rng';
import { COMMANDERS } from './data/commanders';
import { LAYOUT } from './data/ruleset';
import type { Difficulty } from './data/types';
import { Game } from './game/game';
import { botCompany, botLoadout, companyOf, critPercent, grantMatchReward, loadoutFromProfile, loadProfile, saveProfile, upgradeCard, type Profile } from './meta/profile';
import { lanPlayUrl } from './net/lan';
import { onlineConfigured, photonAppId, searchOpponent } from './net/matchmaking';
import { rememberPhotonAppId, resolvePhotonAppId } from './net/photonApp';
import type { FoundMatch } from './net/types';
import { assets } from './render/assets';
import {
  clearMatches,
  exportAllMatches,
  flushOutbox,
  loadMatches,
  loadSettings,
  saveSettings,
  updateMatchFeedback,
  uploadMatch,
  type MatchRecord,
  type Settings,
} from './telemetry/telemetry';
import { Screens, type Setup } from './ui/screens';

const BASE = import.meta.env.BASE_URL;

class Stage {
  readonly canvas: HTMLCanvasElement;
  readonly ctx: CanvasRenderingContext2D;
  private scale = 1;
  private readonly el: HTMLElement;

  constructor() {
    this.el = document.getElementById('stage')!;
    this.canvas = document.getElementById('game') as HTMLCanvasElement;
    this.ctx = this.canvas.getContext('2d', { alpha: false })!;
    window.addEventListener('resize', () => this.resize());
    window.visualViewport?.addEventListener('resize', () => this.resize());
    this.resize();
  }

  resize(): void {
    const vw = window.visualViewport?.width ?? window.innerWidth;
    const vh = window.visualViewport?.height ?? window.innerHeight;
    this.scale = Math.min(vw / LAYOUT.width, vh / LAYOUT.height);
    const w = Math.floor(LAYOUT.width * this.scale);
    const h = Math.floor(LAYOUT.height * this.scale);
    this.el.style.width = `${w}px`;
    this.el.style.height = `${h}px`;
    const dpr = Math.min(2.5, window.devicePixelRatio || 1);
    this.canvas.width = Math.floor(w * dpr);
    this.canvas.height = Math.floor(h * dpr);
    this.ctx.setTransform(this.scale * dpr, 0, 0, this.scale * dpr, 0, 0);
    this.ctx.imageSmoothingEnabled = true;
    this.ctx.imageSmoothingQuality = 'high';
  }

  toLogical = (clientX: number, clientY: number): { x: number; y: number } => {
    const r = this.canvas.getBoundingClientRect();
    return { x: (clientX - r.left) / this.scale, y: (clientY - r.top) / this.scale };
  };

  /** Idle background when no match is running. */
  drawIdle(): void {
    const g = this.ctx;
    g.fillStyle = '#0f0e1c';
    g.fillRect(0, 0, LAYOUT.width, LAYOUT.height);
  }
}

class App {
  private readonly stage = new Stage();
  private readonly screens = new Screens(document.getElementById('ui')!);
  private settings: Settings = loadSettings();
  private profile: Profile = loadProfile();
  private game: Game | null = null;
  private searchToken = 0;
  private setup: Setup = { commander: COMMANDERS[0].id, difficulty: 'recruit', board: 'arrows' };
  private lastRecord: MatchRecord | null = null;

  async boot(): Promise<void> {
    this.screens.loading(0);
    await Promise.all([this.loadFonts(), assets.load(BASE, (p) => this.screens.loading(p * 0.9))]);
    this.screens.loading(1);
    void flushOutbox();
    audio.setSfx(this.settings.sound);
    audio.setMusic(this.settings.music);
    // Unlock audio on the first gesture anywhere.
    const unlock = (): void => {
      audio.init();
      if (this.settings.music) audio.startMusic();
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    this.applyUrlParams();
    await this.ensurePhoton();
    this.showTitle();
  }

  /** One read of the App Id, then it stays in settings on this device. */
  private async ensurePhoton(): Promise<void> {
    if (this.settings.photonAppId.trim()) {
      rememberPhotonAppId(this.settings.photonAppId);
      return;
    }
    const id = await resolvePhotonAppId();
    if (!id) return;
    this.settings.photonAppId = id;
    saveSettings(this.settings);
  }

  private applyUrlParams(): void {
    const q = new URLSearchParams(location.search);
    const seed = q.get('seed');
    if (seed) this.settings.seed = seed;
    const d = q.get('difficulty');
    if (d === 'recruit' || d === 'veteran' || d === 'warlord') this.setup.difficulty = d;
    const board = q.get('board');
    if (board === 'arrows' || board === 'painted') this.setup.board = board;
    const c = q.get('commander');
    if (c && COMMANDERS.some((x) => x.id === c)) this.setup.commander = c;
    const t = q.get('telemetry');
    if (t) {
      this.settings.telemetryUrl = t;
      saveSettings(this.settings);
    }
    const photon = q.get('photon');
    if (photon) {
      this.settings.photonAppId = photon;
      rememberPhotonAppId(photon);
    }
    if (q.get('tutorial') === '0') this.settings.tutorialDone = true;
    if (photon || q.get('tutorial') === '0') saveSettings(this.settings);
  }

  private async loadFonts(): Promise<void> {
    const weights: Array<[string, string]> = [
      ['400', 'Montserrat-Regular'],
      ['500', 'Montserrat-Medium'],
      ['600', 'Montserrat-SemiBold'],
      ['700', 'Montserrat-Bold'],
      ['800', 'Montserrat-ExtraBold'],
    ];
    if (!('FontFace' in window)) return;
    await Promise.all(
      weights.map(async ([weight, file]) => {
        try {
          const face = new FontFace('Montserrat', `url(${BASE}fonts/${file}.ttf)`, { weight });
          await face.load();
          document.fonts.add(face);
        } catch {
          /* fall back to system font */
        }
      }),
    );
  }

  // --------------------------------------------------------------- screens

  private showTitle(): void {
    this.stopGame();
    this.stage.drawIdle();
    if (this.settings.music) audio.startMusic();
    audio.intensity = 0.1;
    const tutorialPending = !this.settings.tutorialDone;
    this.screens.title(
      {
        play: () => {
          audio.play('click');
          this.showSetup('online');
        },
        pve: () => {
          audio.play('click');
          if (tutorialPending) this.startBotMatch({ commander: COMMANDERS[0].id, difficulty: 'recruit', board: 'arrows' }, true);
          else this.showSetup('pve');
        },
        howto: () => {
          audio.play('click');
          this.screens.howto(() => this.showTitle());
        },
        stats: () => {
          audio.play('click');
          this.showStats();
        },
        settings: () => {
          audio.play('click');
          this.showSettings(() => this.showHub());
        },
        units: () => {
          audio.play('click');
          this.showUnits();
        },
        store: () => {
          audio.play('click');
          this.showStore();
        },
      },
      tutorialPending,
      { gold: this.profile.gold, crit: critPercent(this.profile), online: onlineConfigured(this.settings) },
    );
  }

  private showHub(): void {
    this.showTitle();
  }

  private hubHandlers(): {
    play: () => void;
    stats: () => void;
    settings: () => void;
    home: () => void;
    units: () => void;
    store: () => void;
  } {
    return {
      play: () => {
        audio.play('click');
        if (!this.settings.tutorialDone) this.startBotMatch({ commander: COMMANDERS[0].id, difficulty: 'recruit', board: 'arrows' }, true);
        else this.showSetup('online');
      },
      stats: () => {
        audio.play('click');
        this.showStats();
      },
      settings: () => {
        audio.play('click');
        this.showSettings(() => this.showHub());
      },
      home: () => {
        audio.play('click');
        this.showTitle();
      },
      units: () => {
        audio.play('click');
        this.showUnits();
      },
      store: () => {
        audio.play('click');
        this.showStore();
      },
    };
  }

  private showUnits(): void {
    this.stopGame();
    this.stage.drawIdle();
    this.screens.units(this.profile, {
      ...this.hubHandlers(),
      upgrade: (id) => {
        if (upgradeCard(this.profile, id)) audio.play('shop');
        else audio.play('error');
      },
      saveCompany: (deck, bosses) => {
        this.profile.deck = deck;
        this.profile.bosses = bosses;
        saveProfile(this.profile);
        audio.play('click');
      },
    });
  }

  private showStore(): void {
    this.stopGame();
    this.stage.drawIdle();
    this.screens.store(this.hubHandlers(), { gold: this.profile.gold, crit: critPercent(this.profile) });
  }

  private setupGen = 0;
  private battleMode: 'online' | 'pve' = 'online';

  private showSetup(mode: 'online' | 'pve' = 'online'): void {
    const gen = ++this.setupGen;
    const seedLabel = this.settings.seed ? this.settings.seed : 'random';
    const photon = photonAppId(this.settings);
    void lanPlayUrl(photon).then((playUrl) => {
      if (gen !== this.setupGen) return;
      const photonReady = photonAppId(this.settings) !== '';
      const onlineNote = photonReady
        ? 'Photon hosts the match. Your friend opens this page and presses Start battle. They wait up to 60 seconds, then a bot takes the empty seat.'
        : playUrl
          ? 'Open the link below on the other phone or iPad, then press Start battle on both. They look for each other for up to 60 seconds.'
          : 'Open this page on the other phone and both press Start battle. They look for each other for up to 60 seconds.';
      this.screens.setup(this.setup, seedLabel, {
        back: () => {
          audio.play('click');
          this.showTitle();
        },
        start: (s, chosen) => {
          audio.play('click');
          this.setup = s;
          this.battleMode = chosen;
          if (chosen === 'pve') {
            this.startBotMatch(s, false);
            return;
          }
          try {
            this.beginBattle(s);
          } catch (err) {
            console.error(err);
            this.screens.searching({ secondsLeft: 0, detail: 'Could not open matchmaking. Training against a bot.', found: true }, () => {});
            window.setTimeout(() => this.startBotMatch(s, false), 700);
          }
        },
      }, onlineNote, playUrl, mode);
    });
  }

  private showStats(): void {
    this.screens.stats(loadMatches(), {
      back: () => this.showHub(),
      export: () => exportAllMatches(),
      clear: () => {
        clearMatches();
        this.showStats();
      },
    });
  }

  private showSettings(back: () => void): void {
    this.screens.settings(this.settings, {
      back,
      change: (s) => {
        this.settings = s;
        if (s.photonAppId) rememberPhotonAppId(s.photonAppId);
        saveSettings(s);
        audio.setSfx(s.sound);
        audio.setMusic(s.music);
      },
      resetTutorial: () => {
        this.settings.tutorialDone = false;
        saveSettings(this.settings);
      },
    });
  }

  // ----------------------------------------------------------------- match

  private beginBattle(setup: Setup): void {
    this.setup = setup;
    if (!this.settings.tutorialDone) {
      this.startBotMatch(setup, true);
      return;
    }
    this.searchToken++;
    const token = this.searchToken;
    const mine = loadoutFromProfile(this.profile);
    const company = companyOf(this.profile);
    const search = searchOpponent({
      settings: this.settings,
      commander: setup.commander,
      loadout: mine,
      deck: company.deck,
      bosses: company.bosses,
      board: setup.board,
      onStatus: (detail, secondsLeft, found) => {
        if (token !== this.searchToken) return;
        this.screens.searching({ detail, secondsLeft, found }, () => {
          audio.play('click');
          search.cancel();
          this.searchToken++;
          this.showSetup();
        });
      },
    });
    void search.promise.then((found) => {
      if (token !== this.searchToken || search.cancelled) return;
      if (!found) {
        this.screens.searching({ secondsLeft: 0, detail: 'No commander is waiting. Training against a bot.', found: true }, () => {});
        window.setTimeout(() => {
          if (token !== this.searchToken) return;
          this.startBotMatch(setup, false);
        }, 700);
        return;
      }
      window.setTimeout(() => {
        if (token !== this.searchToken) return;
        this.startOnlineMatch(setup, found);
      }, 800);
    });
  }

  private relaunch(): void {
    if (this.battleMode === 'pve') this.startBotMatch(this.setup, false);
    else this.beginBattle(this.setup);
  }

  private startBotMatch(setup: Setup, tutorial: boolean): void {
    const seed = this.settings.seed ? seedFromString(this.settings.seed) : randomSeed();
    const company = companyOf(this.profile);
    const bot = botCompany(seed);
    const others = COMMANDERS.filter((c) => c.id !== setup.commander);
    const aiCommander = others[seed % others.length].id;
    if (tutorial) {
      this.settings.tutorialDone = true;
      saveSettings(this.settings);
    } else this.battleMode = 'pve';
    this.launch(setup, {
      seed,
      playerCommander: setup.commander,
      aiCommander,
      difficulty: setup.difficulty,
      opponent: 'ai',
      board: setup.board,
      loadout: [loadoutFromProfile(this.profile), botLoadout(setup.difficulty)],
      decks: [company.deck, bot.deck],
      bosses: [company.bosses, bot.bosses],
    }, tutorial, null);
  }

  private startOnlineMatch(setup: Setup, found: FoundMatch): void {
    this.battleMode = 'online';
    const side = found.link.localSide;
    const mine = setup.commander;
    const theirs = found.link.opponentCommander;
    this.launch(setup, {
      seed: found.seed,
      playerCommander: side === 0 ? mine : theirs,
      aiCommander: side === 1 ? mine : theirs,
      difficulty: setup.difficulty,
      opponent: 'human',
      board: found.board ?? setup.board,
      loadout: found.loadout,
      decks: found.decks,
      bosses: found.bosses,
    }, false, found.link);
  }

  private launch(
    setup: Setup,
    config: ConstructorParameters<typeof Game>[2],
    tutorial: boolean,
    link: FoundMatch['link'] | null,
  ): void {
    this.setup = setup;
    this.stopGame();
    this.screens.clear();
    const game = new Game(
      this.stage.canvas,
      this.stage.ctx,
      config,
      setup.difficulty,
      {
        onEnd: (record) => this.onMatchEnd(record),
        onPause: () => this.pause(),
      },
      this.stage.toLogical,
      tutorial,
      link,
    );
    this.game = game;
    (window as unknown as { peasents: unknown }).peasents = { game, commands: game.commands, match: game.match };
    if (this.settings.music) audio.startMusic();
    game.start();
  }

  private stopGame(): void {
    if (this.game) {
      this.game.stop();
      this.game = null;
    }
  }

  private pause(): void {
    if (!this.game) return;
    this.game.setPaused(true);
    const live = this.game.live;
    const show = (): void =>
      this.screens.pause(this.settings, {
        resume: () => {
          audio.play('click');
          this.screens.clear();
          this.game?.setPaused(false);
        },
        restart: () => {
          audio.play('click');
          this.stopGame();
          this.relaunch();
        },
        howto: () => {
          audio.play('click');
          this.screens.howto(show);
        },
        quit: () => {
          audio.play('click');
          this.showTitle();
        },
        toggleSound: () => {
          this.settings.sound = !this.settings.sound;
          saveSettings(this.settings);
          audio.setSfx(this.settings.sound);
          audio.play('click');
          show();
        },
        toggleMusic: () => {
          this.settings.music = !this.settings.music;
          saveSettings(this.settings);
          audio.setMusic(this.settings.music);
          if (this.settings.music) audio.startMusic();
          show();
        },
      }, live ? 'This is a live match. It keeps running while this menu is open. Restarting leaves your opponent.' : '');
    show();
  }

  private onMatchEnd(record: MatchRecord): void {
    this.lastRecord = record;
    this.settings.matchesPlayed++;
    saveSettings(this.settings);
    audio.intensity = 0.1;
    const reward = grantMatchReward(this.profile, record.summary.result);
    this.screens.results(record, {
      again: () => {
        audio.play('click');
        this.relaunch();
      },
      menu: () => {
        audio.play('click');
        this.showTitle();
      },
      export: () => exportAllMatches(),
      feedback: (f) => {
        audio.play('shop');
        const updated = updateMatchFeedback(record.id, f);
        if (updated) void uploadMatch(updated);
      },
    }, reward);
  }

  get currentRecord(): MatchRecord | null {
    return this.lastRecord;
  }
}

const app = new App();
void app.boot();

declare global {
  interface Window {
    peasents?: unknown;
  }
}

export type { Difficulty };
